// Mastery-spine (Progress) API client.
//
// Backend: backend/domain/Models.Write/Progress/  +  Models.Read/Progress/
//   POST /api/progress/start             → 202 (fire-and-forget)
//   POST /api/progress/select-element    { element } → 202 (fire-and-forget)
//   POST /api/progress/roll-back         → 202 (go back one week, destructive)
//   POST /api/progress/recordings/swap   { dropRecordingId } → 202 (drop a retained take)
//   POST /api/progress/practice-rep      { practice, recordingId }
//   POST /api/recordings/{id}/notes        { notes } (per-session notes)
//   POST /api/query  $type:'FindMasteryProgressByUserQuery'  → MasteryProgress[]
//
// The state-mutating commands (start / select-element / roll-back / swap /
// abandon) are fire-and-forget (wiki/feature-frontend-cqrs-dataflow.md §4): they
// return 202 with no body, and the page updates only from the read channel — a
// `MasteryProgress` push + refetch through the `/query` GET above. The command
// response (`null` on a 202) is never read to mutate displayed state.
//
// Weeks advance automatically by the clock (7-day windows, server-scheduled);
// there is no manual complete-week action.
//
// Surfaces both halves of the mastery spine (wiki/product-vision.md §"The
// mastery spine"): the 12-Week Plan and the 4 Stages of Learning. The plan
// stream id IS the user id — the backend derives it from the JWT, so the
// query's `userId` only needs to be a syntactically valid Guid the caller
// owns; commands ignore any body id entirely.
//
// Thin wrappers over the generated `@voicedojo/backend-types/progress`
// contract — kept here (not in the generated package) so the page imports a
// stable `src/lib` path and gets `ApiResult` ergonomics.

import type { MasteryProgress } from '@voicedojo/backend-types/progress';
import { findMasteryProgressByUser } from '@voicedojo/backend-types/progress';
import {
  ProgressKeys,
  UiKeys,
  Practice,
  LearningStage,
  MasteryElement,
  type CompletedWeek,
  type PlanStatus,
  type WeekRecording,
} from '@voicedojo/backend-types';

// Re-export the generated QueryDef so callers can use observableQuery
// (wiki/feature-frontend-cqrs-dataflow.md §2a) without chasing the package path.
export { findMasteryProgressByUser };
import { t } from '$lib/i18n';
import { api } from './client';
import { bareRecordingId, practiceLabel } from './recordings';

export type {
  CompletedWeek,
  LearningStage,
  MasteryElement,
  PlanStatus,
  WeekRecording,
};

export type { Practice };

/**
 * Human label for a practice — re-exported from the session client so the
 * Progress page imports a single label accessor. Resolves via `t()` over the
 * generated `Ui.PracticeLabel.*` keys (no hardcoded map).
 */
export { practiceLabel };

/**
 * The practice-rep counter (wiki/feature-progress.md §"The practice-rep
 * counter"). Narrows the generated `RepTally.perPractice` (emitted as
 * `unknown` because it's a dictionary keyed by the `Practice` enum) to a
 * typed partial record — a practice the user has never logged a rep for is
 * simply absent.
 */
export interface RepTally {
  thisWeek: number;
  lifetime: number;
  perPractice: Partial<Record<Practice, number>>;
  weeklyTarget: number;
}

/**
 * The Progress read model with `reps` narrowed to the typed {@link RepTally}.
 * The generated `MasteryProgress.reps` is structurally identical but carries
 * `perPractice: unknown`; this view restores the key/value types for the page.
 */
export type MasteryProgressView = Omit<MasteryProgress, 'reps'> & {
  reps: RepTally;
};

export type { MasteryProgress };

/** Mastery elements in canonical display order — the generated enum's order. */
export const MASTERY_ELEMENTS: readonly MasteryElement[] = Object.values(MasteryElement);

/**
 * The 4 stages in climbing order — index doubles as the 0-3 stage rank. Order is
 * the generated `LearningStage` enum's declaration order (the canonical climb).
 */
export const LEARNING_STAGES: readonly LearningStage[] = Object.values(LearningStage);

/**
 * Practices in display order — drives the per-practice rep breakdown. A deliberate
 * subset of the generated `Practice` enum: `Standalone` (upload-only, no practice
 * page) is excluded, so this stays an explicit list rather than `Object.values`.
 */
export const PRACTICES: readonly Practice[] = [
  Practice.TheInstrument,
  Practice.ArchetypePlay,
  Practice.BodyLanguagePlay,
  Practice.Storytelling,
  Practice.RecordAndReview,
  Practice.ActiveListening,
  Practice.Riff,
];

/** Human label for a mastery element — resolved via `t()` over `Progress.MasteryElement.*`. */
export function elementLabel(element: MasteryElement): string {
  return t(ProgressKeys.MasteryElement[element]);
}

/** Human label for a learning stage — resolved via `t()` over `Ui.LearningStage.*`. */
export function stageLabel(stage: LearningStage): string {
  return t(UiKeys.LearningStage[stage]);
}

/** The 0-3 rank of a learning stage — drives progress-bar fill. */
export function stageRank(stage: LearningStage): number {
  const rank = LEARNING_STAGES.indexOf(stage);
  return rank < 0 ? 0 : rank;
}

/**
 * Fetch the caller's mastery-spine progress. Resolves `null` when the user has
 * not started a plan yet (the query result is an empty array). Goes through
 * `POST /api/query` ($type: FindMasteryProgressByUserQuery). The query carries no
 * user field — the dispatcher scopes it to the caller's own owner server-side
 * (resource-authorization), so the result is always the caller's own progress.
 * Throws `ApiError` on a transport failure.
 */
export async function getMasteryProgress(): Promise<MasteryProgressView | null> {
  const rows = await api.post<MasteryProgressView[]>('/query', {
    $type: findMasteryProgressByUser.$type,
  });
  return rows[0] ?? null;
}

/**
 * Start the caller's 12-Week mastery plan. Idempotent server-side. Fire-and-
 * forget (202, no body) — the page reflects the new plan from the read channel,
 * never from this call's response. Throws `ApiError` only on a synchronous
 * validation/authorization failure.
 */
export async function startMasteryPlan(): Promise<void> {
  await api.post<unknown>('/progress/start', {}, { requiresAuth: true });
}

/** Pick the element to drill for the current week. Throws `ApiError`. */
export async function selectWeekElement(element: MasteryElement): Promise<void> {
  await api.post<unknown>('/progress/select-element', { element }, { requiresAuth: true });
}

/**
 * Go back one week to re-drill — destructive and non-recoverable (discards the
 * current week + restarts the prior week empty; rolls competence back one
 * step). One decrement per call; refused server-side at week 1. Throws
 * `ApiError`. See wiki/feature-progress.md §"Going back a week".
 */
export async function rollBackWeek(): Promise<void> {
  await api.post<unknown>('/progress/roll-back', {}, { requiresAuth: true });
}

/**
 * End the caller's 12-Week plan early — **non-destructive**. Progress earned
 * (lifetime competence + reps + the archived week history) is kept; only
 * auto-tracking + recording retention stop. The plan reads `status:
 * 'Abandoned'` afterward (start a new plan with {@link startMasteryPlan}).
 * Refused server-side (409) when there is no active plan. Throws `ApiError`.
 * See wiki/feature-progress.md §"Ending a plan early (abandon)".
 */
export async function abandonPlan(): Promise<void> {
  await api.post<unknown>('/progress/abandon', {}, { requiresAuth: true });
}

/**
 * Set (or clear) the free-text notes on a recording — surfaced read-first in the
 * completed-week detail view (editable, not encouraged). Premium-only path on
 * the page. `POST /api/recordings/{id}/notes`. Throws `ApiError`.
 */
export async function setRecordingNotes(
  recordingId: string,
  notes: string,
): Promise<void> {
  await api.post<unknown>(
    `/recordings/${recordingId}/notes`,
    {
      id: recordingId,
      notes,
    },
    { requiresAuth: true },
  );
}

/**
 * Log a practice rep — fired once per session when the practice crosses the
 * 50% mark of its set length (wiki/feature-progress.md §"The practice-rep
 * counter"). Idempotent server-side per `recordingId`. An offline take's
 * `offline:` id goes on the wire bare, the same Guid an explicit Save would use;
 * only the practice and that id leave the device. The plan stream id is
 * derived from the JWT server-side, so no caller-supplied user id is needed
 * (the command's body `id` is informational only — omitted here). Best-effort:
 * callers fire this from the recording-timer seam and swallow failures (a
 * missed rep must never disrupt the practice). Throws `ApiError` on a
 * transport failure for the caller to swallow.
 */
export async function logPracticeRep(
  practice: Practice,
  recordingId: string,
): Promise<void> {
  await api.post<unknown>(
    '/progress/practice-rep',
    {
      practice,
      recordingId: bareRecordingId(recordingId),
    },
    { requiresAuth: true },
  );
}
